import { PortfolioPageAttributes } from '@/types'
import { UpdatePortfolioPageMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditPortfolioPageQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Button } from '@/components/base/button'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Inline as Error } from '@/components/error'
import { Editor } from './editor'

type PortfolioPage = EditPortfolioPageQuery['portfolioPages']['nodes'][0]

interface Props {
  id: string
  onSuccess: () => void
}

export const Form = ({ id, onSuccess }: Props) => {
  const {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading
  } = useForm<UpdatePortfolioPageMutation, PortfolioPageAttributes, EditPortfolioPageQuery, PortfolioPage>({
    id,
    initializer: portfolioPage => ({
      eventId: portfolioPage.event.id,
      title: portfolioPage.title,
      description: portfolioPage.description,
      slug: portfolioPage.slug,
      enabled: portfolioPage.enabled,
      body: portfolioPage.body || '',
    }),
    query: EditQuery,
    queryKey: 'portfolioPages',
    mutation: UpdateMutation,
    resultKey: 'updatePortfolioPage',
    refetchQueries: [TableQuery]
  })

  const [showDelete, setShowDelete] = useState(false)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  const onDelete = () => {
    setShowDelete(false)
    onSuccess()
  }

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Title', field: 'title', type: 'text' }) }
          { formBuilder.buildField({ name: 'Description', field: 'description', type: 'text' }) }
          { formBuilder.buildField({ name: 'Slug', field: 'slug', type: 'text' }) }
          { formBuilder.buildField({ name: 'Enabled', field: 'enabled', type: 'switch' }) }
        </div>
        { formBuilder.buildCustomField({
          name: 'Content',
          field: 'body',
          input: <Editor content={attributes.body} onChange={body => setAttributes(attrs => ({ ...attrs, body }))} />
        })}
        <div className="flex flex-row justify-between">
          <Button color="red" onClick={() => setShowDelete(true) }>Delete</Button>
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
        </div>
        <DeleteDialog
          id={id}
          mutation={DeleteMutation}
          open={showDelete}
          refetchQueries={[TableQuery]}
          setOpen={setShowDelete}
          onSuccess={onDelete}
        />
      </div>
    </>
  )
}
